import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { SectionHeader } from "../components/UI";
import { TID } from "../constants/testIds";
import { toast } from "sonner";
import {
  Building2, Contact, Palette, ShieldCheck, ClipboardCheck, ChevronLeft, ChevronRight,
  Check, Upload, MapPin, Users, Save,
} from "lucide-react";

const INDUSTRIES = ["Technology", "Financial Services", "Healthcare", "Retail", "Manufacturing", "Education", "Government", "Media", "Energy", "Non-profit", "Other"];
const SIZES = [
  { value: "startup", label: "Startup (1-50)" },
  { value: "smb", label: "SMB (51-250)" },
  { value: "mid", label: "Mid-market (251-1000)" },
  { value: "enterprise", label: "Enterprise (1001-5000)" },
  { value: "large_enterprise", label: "Large Enterprise (5001+)" },
];
const STEPS = [
  { key: "basics", title: "Basics", icon: Building2, label: "Legal & industry" },
  { key: "location", title: "Location", icon: MapPin, label: "Address & contact" },
  { key: "contacts", title: "Contacts", icon: Contact, label: "Key personnel" },
  { key: "org", title: "Org", icon: Users, label: "Departments & sites" },
  { key: "branding", title: "Branding", icon: Palette, label: "Logo & identity" },
  { key: "review", title: "Review", icon: ClipboardCheck, label: "Confirm & save" },
];

export default function CompanyProfile() {
  const { user, tenant, refreshMe } = useAuth();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    legal_name: "", website: "", industry: "Technology", size: "mid", country: "",
    address_line_1: "", address_line_2: "", city: "", state: "", postal_code: "",
    phone: "", tax_id: "",
    contact_person: "", contact_email: "", contact_phone: "",
    hr_head_name: "", hr_head_email: "",
    dpo_name: "", dpo_email: "",
    departments: [], locations: [],
    logo_url: "",
  });
  const [depInput, setDepInput] = useState("");
  const [locInput, setLocInput] = useState("");
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!tenant) return;
    setForm((f) => ({
      ...f,
      legal_name: tenant.legal_name || tenant.name || "",
      website: tenant.website || "",
      industry: tenant.industry || "Technology",
      size: tenant.size || "mid",
      country: tenant.country || "",
      address_line_1: tenant.address_line_1 || "",
      address_line_2: tenant.address_line_2 || "",
      city: tenant.city || "",
      state: tenant.state || "",
      postal_code: tenant.postal_code || "",
      phone: tenant.phone || "",
      tax_id: tenant.tax_id || "",
      contact_person: tenant.contact_person || "",
      contact_email: tenant.contact_email || "",
      contact_phone: tenant.contact_phone || "",
      hr_head_name: tenant.hr_head_name || "",
      hr_head_email: tenant.hr_head_email || "",
      dpo_name: tenant.dpo_name || "",
      dpo_email: tenant.dpo_email || "",
      departments: tenant.departments || [],
      locations: tenant.locations || [],
      logo_url: tenant.logo_url || "",
    }));
  }, [tenant]);

  const completion = useMemo(() => {
    const fields = ["legal_name", "website", "industry", "country", "address_line_1", "city",
      "phone", "contact_person", "contact_email", "hr_head_name", "hr_head_email", "dpo_email", "logo_url"];
    const filled = fields.filter(f => (form[f] || "").trim().length > 0).length + (form.departments.length > 0 ? 1 : 0);
    return Math.round((filled / (fields.length + 1)) * 100);
  }, [form]);

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const addDep = () => {
    if (!depInput.trim()) return;
    setF("departments", [...form.departments, depInput.trim()]);
    setDepInput("");
  };
  const addLoc = () => {
    if (!locInput.trim()) return;
    setF("locations", [...form.locations, locInput.trim()]);
    setLocInput("");
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("Logo must be under 5MB");
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/tenants/${user.tenant_id}/logo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setF("logo_url", data.logo_url);
      toast.success("Logo uploaded");
    } catch { toast.error("Logo upload failed"); }
    finally { setUploadingLogo(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/tenants/${user.tenant_id}/profile`, form);
      toast.success("Company profile saved");
      refreshMe();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  const canProceed = useMemo(() => {
    switch (step) {
      case 0: return form.legal_name.trim().length > 1;
      case 1: return true;
      case 2: return true;
      case 3: return true;
      case 4: return true;
      default: return true;
    }
  }, [step, form]);

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const back = () => setStep((s) => Math.max(0, s - 1));

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <SectionHeader
        title="Company Profile Setup"
        subtitle="Complete your organization profile. This drives compliance letters, invoices, and admin routing."
        right={
          <div className="text-right">
            <div className="text-[10px] font-mono text-slate-500 uppercase tracking-wider">Completion</div>
            <div className="font-display text-2xl font-semibold text-slate-900">{completion}%</div>
          </div>
        }
      />

      <div className="mb-4 h-1.5 bg-slate-100">
        <motion.div
          className="h-full bg-blue-600"
          initial={{ width: 0 }}
          animate={{ width: `${completion}%` }}
          transition={{ type: "spring", stiffness: 120, damping: 22 }}
        />
      </div>

      <Stepper step={step} steps={STEPS}/>

      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.18 }}
          className="mt-6"
        >
          {step === 0 && <StepBasics form={form} setF={setF}/>}
          {step === 1 && <StepLocation form={form} setF={setF}/>}
          {step === 2 && <StepContacts form={form} setF={setF}/>}
          {step === 3 && (
            <StepOrg
              form={form}
              setF={setF}
              depInput={depInput} setDepInput={setDepInput} addDep={addDep}
              locInput={locInput} setLocInput={setLocInput} addLoc={addLoc}
            />
          )}
          {step === 4 && (
            <StepBranding
              form={form}
              uploadLogo={uploadLogo}
              uploadingLogo={uploadingLogo}
              fileRef={fileRef}
            />
          )}
          {step === 5 && <StepReview form={form} completion={completion}/>}
        </motion.div>
      </AnimatePresence>

      <div className="mt-8 pt-5 border-t border-slate-200 flex items-center justify-between">
        <button type="button" onClick={back} disabled={step === 0} className="btn-ghost disabled:opacity-40">
          <ChevronLeft size={14}/> Back
        </button>
        <div className="text-[11px] font-mono text-slate-500">STEP {step + 1} / {STEPS.length}</div>
        <div className="flex items-center gap-2">
          <button className="btn-ghost" onClick={save} disabled={saving} data-testid={TID.cpSave}>
            <Save size={14}/> Save progress
          </button>
          {step < STEPS.length - 1 ? (
            <button className="btn-primary" onClick={next} disabled={!canProceed} data-testid={TID.cpNext}>
              Continue <ChevronRight size={14}/>
            </button>
          ) : (
            <button className="btn-primary" onClick={save} disabled={saving}>
              <Check size={14}/> {saving ? "Saving…" : "Save & finish"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Stepper({ step, steps }) {
  return (
    <div className="flex items-center gap-2 overflow-x-auto pb-1">
      {steps.map((s, i) => {
        const active = i === step;
        const done = i < step;
        const Icon = s.icon;
        return (
          <div key={s.key} className="flex items-center gap-2 shrink-0">
            <motion.div
              animate={{ scale: active ? 1.05 : 1 }}
              className={
                "w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-semibold border " +
                (active
                  ? "bg-blue-600 text-white border-blue-600"
                  : done ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-500 border-slate-300")
              }
            >
              {done ? <Check size={12} strokeWidth={3}/> : <Icon size={12} strokeWidth={2}/>}
            </motion.div>
            <div className="flex flex-col leading-tight">
              <span className="text-[10px] font-mono uppercase tracking-wider text-slate-400">{s.title}</span>
              <span className={"text-xs font-medium " + (active ? "text-slate-900" : "text-slate-600")}>{s.label}</span>
            </div>
            {i < steps.length - 1 && <div className="w-6 md:w-10 h-px bg-slate-300"/>}
          </div>
        );
      })}
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="eyebrow block mb-1">{label}</label>
      {children}
      {hint && <div className="text-[11px] text-slate-500 mt-1">{hint}</div>}
    </div>
  );
}

function StepBasics({ form, setF }) {
  return (
    <div className="space-y-4">
      <Field label="Legal entity name">
        <input value={form.legal_name} onChange={(e) => setF("legal_name", e.target.value)} className="input" data-testid={TID.cpLegalName}/>
      </Field>
      <div className="grid md:grid-cols-2 gap-3">
        <Field label="Website">
          <input value={form.website} onChange={(e) => setF("website", e.target.value)} placeholder="https://…" className="input" data-testid={TID.cpWebsite}/>
        </Field>
        <Field label="Tax / Registration ID">
          <input value={form.tax_id} onChange={(e) => setF("tax_id", e.target.value)} placeholder="e.g. EIN, VAT, CIN" className="input"/>
        </Field>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        <Field label="Industry">
          <select value={form.industry} onChange={(e) => setF("industry", e.target.value)} className="input">
            {INDUSTRIES.map(i => <option key={i}>{i}</option>)}
          </select>
        </Field>
        <Field label="Company size">
          <select value={form.size} onChange={(e) => setF("size", e.target.value)} className="input">
            {SIZES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </Field>
      </div>
    </div>
  );
}

function StepLocation({ form, setF }) {
  return (
    <div className="space-y-4">
      <Field label="Country">
        <input value={form.country} onChange={(e) => setF("country", e.target.value)} placeholder="United States" className="input"/>
      </Field>
      <Field label="Registered address">
        <input value={form.address_line_1} onChange={(e) => setF("address_line_1", e.target.value)} placeholder="Street address" className="input mb-2"/>
        <input value={form.address_line_2} onChange={(e) => setF("address_line_2", e.target.value)} placeholder="Suite / floor (optional)" className="input"/>
      </Field>
      <div className="grid md:grid-cols-3 gap-3">
        <Field label="City">
          <input value={form.city} onChange={(e) => setF("city", e.target.value)} className="input"/>
        </Field>
        <Field label="State / region">
          <input value={form.state} onChange={(e) => setF("state", e.target.value)} className="input"/>
        </Field>
        <Field label="Postal code">
          <input value={form.postal_code} onChange={(e) => setF("postal_code", e.target.value)} className="input font-mono"/>
        </Field>
      </div>
      <Field label="Main phone">
        <input value={form.phone} onChange={(e) => setF("phone", e.target.value)} placeholder="+1 555 000 0000" className="input font-mono"/>
      </Field>
    </div>
  );
}

function StepContacts({ form, setF }) {
  return (
    <div className="space-y-6">
      <div className="border border-slate-200 p-4">
        <div className="eyebrow mb-3">Primary contact</div>
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Contact person">
            <input value={form.contact_person} onChange={(e) => setF("contact_person", e.target.value)} className="input"/>
          </Field>
          <Field label="Contact email">
            <input type="email" value={form.contact_email} onChange={(e) => setF("contact_email", e.target.value)} className="input"/>
          </Field>
          <Field label="Contact phone">
            <input value={form.contact_phone} onChange={(e) => setF("contact_phone", e.target.value)} className="input font-mono"/>
          </Field>
        </div>
      </div>

      <div className="border border-slate-200 p-4">
        <div className="eyebrow mb-3">Head of HR</div>
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Name">
            <input value={form.hr_head_name} onChange={(e) => setF("hr_head_name", e.target.value)} className="input"/>
          </Field>
          <Field label="Email">
            <input type="email" value={form.hr_head_email} onChange={(e) => setF("hr_head_email", e.target.value)} className="input"/>
          </Field>
        </div>
      </div>

      <div className="border border-slate-200 p-4">
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck size={14}/>
          <div className="eyebrow">Data Protection Officer (GDPR)</div>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="DPO name">
            <input value={form.dpo_name} onChange={(e) => setF("dpo_name", e.target.value)} className="input"/>
          </Field>
          <Field label="DPO email">
            <input type="email" value={form.dpo_email} onChange={(e) => setF("dpo_email", e.target.value)} className="input"/>
          </Field>
        </div>
      </div>
    </div>
  );
}

function StepOrg({ form, setF, depInput, setDepInput, addDep, locInput, setLocInput, addLoc }) {
  return (
    <div className="space-y-5">
      <div>
        <div className="eyebrow mb-2">Departments</div>
        <div className="flex flex-wrap gap-2 mb-2">
          {form.departments.map((d, i) => (
            <span key={d + i} className="pill pill-closed flex items-center gap-1">
              {d}
              <button onClick={() => setF("departments", form.departments.filter((_, j) => j !== i))}>×</button>
            </span>
          ))}
          {form.departments.length === 0 && <span className="text-xs text-slate-500">No departments yet</span>}
        </div>
        <div className="flex gap-2">
          <input
            value={depInput}
            onChange={(e) => setDepInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addDep())}
            placeholder="e.g. Engineering"
            className="input"
          />
          <button className="btn-ghost" onClick={addDep}>Add</button>
        </div>
      </div>

      <div>
        <div className="eyebrow mb-2">Office locations</div>
        <div className="flex flex-wrap gap-2 mb-2">
          {form.locations.map((d, i) => (
            <span key={d + i} className="pill pill-closed flex items-center gap-1">
              {d}
              <button onClick={() => setF("locations", form.locations.filter((_, j) => j !== i))}>×</button>
            </span>
          ))}
          {form.locations.length === 0 && <span className="text-xs text-slate-500">No locations yet</span>}
        </div>
        <div className="flex gap-2">
          <input
            value={locInput}
            onChange={(e) => setLocInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addLoc())}
            placeholder="e.g. San Francisco HQ"
            className="input"
          />
          <button className="btn-ghost" onClick={addLoc}>Add</button>
        </div>
      </div>
    </div>
  );
}

function StepBranding({ form, uploadLogo, uploadingLogo, fileRef }) {
  return (
    <div className="space-y-5">
      <div className="border border-slate-200 p-6 flex items-center gap-6">
        <div className="w-24 h-24 border border-slate-300 flex items-center justify-center bg-slate-50">
          {form.logo_url ? (
            <img src={`${process.env.REACT_APP_BACKEND_URL}${form.logo_url}`} alt="logo" className="max-w-full max-h-full object-contain"/>
          ) : (
            <Palette size={22} strokeWidth={1.75} className="text-slate-400"/>
          )}
        </div>
        <div className="flex-1">
          <div className="eyebrow mb-1">Company logo</div>
          <div className="text-xs text-slate-500 mb-3">PNG, JPG, SVG or WEBP · under 5MB · used across the app and invoices.</div>
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/svg+xml"
            onChange={(e) => uploadLogo(e.target.files?.[0])}
            className="hidden"
            data-testid={TID.cpLogoInput}
          />
          <button className="btn-primary" onClick={() => fileRef.current?.click()} disabled={uploadingLogo}>
            <Upload size={14}/> {uploadingLogo ? "Uploading…" : (form.logo_url ? "Replace logo" : "Upload logo")}
          </button>
        </div>
      </div>
    </div>
  );
}

function StepReview({ form, completion }) {
  return (
    <div className="space-y-4">
      <div className="border border-slate-200 bg-slate-50 p-5">
        <div className="eyebrow mb-3">Preview</div>
        <div className="grid md:grid-cols-2 gap-x-6 gap-y-3 text-sm">
          <Row label="Legal name" value={form.legal_name || ","}/>
          <Row label="Website" value={form.website || ","} mono/>
          <Row label="Industry" value={form.industry}/>
          <Row label="Size" value={form.size} mono/>
          <Row label="Country" value={form.country || ","}/>
          <Row label="Phone" value={form.phone || ","} mono/>
          <Row label="Address" value={[form.address_line_1, form.city, form.state, form.postal_code].filter(Boolean).join(", ") || ","}/>
          <Row label="Contact" value={form.contact_person ? `${form.contact_person} · ${form.contact_email}` : ","}/>
          <Row label="HR head" value={form.hr_head_name ? `${form.hr_head_name} · ${form.hr_head_email}` : ","}/>
          <Row label="DPO" value={form.dpo_email || ","} mono/>
          <Row label="Departments" value={form.departments.join(", ") || ","}/>
          <Row label="Locations" value={form.locations.join(", ") || ","}/>
        </div>
      </div>
      <div className="border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
        Profile completion: <span className="font-semibold">{completion}%</span>. Saving now writes to your tenant record and updates the completion counter used across the platform.
      </div>
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-wider text-slate-400 mb-0.5">{label}</div>
      <div className={"text-slate-900 " + (mono ? "font-mono" : "")}>{value}</div>
    </div>
  );
}
